import * as THREE from 'three';
import { Input } from './core/Input';
import { CapsuleWorld } from './physics/CapsuleWorld';
import { PlayerController } from './player/PlayerController';
import { PlayerAvatar } from './player/PlayerAvatar';
import { Viewmodel } from './player/Viewmodel';
import { AudioManager } from './audio/AudioManager';
import { buildFacilityArena, isInBathroom, isInVent } from './world/FacilityArena';
import {
  getMirrorQuality,
  mirrorLastAgeMs,
  setMirrorQuality,
  setMirrorScenePrep,
  setMirrorsLive,
  warmMirrors,
  type FacilityRoom,
} from './world/FacilityDressing';
import { buildViewmodelStudio } from './world/ViewmodelStudio';
import { PickupSystem, type PickupKind } from './world/PickupSystem';
import { DoorSystem } from './world/DoorSystem';
import { LampFlicker } from './world/LampFlicker';
import { RoomLampDim } from './world/RoomLampDim';
import { bakeFacilityLighting } from './world/BakeLights';
import { RoomCuller, floorBelow, roomAt } from './world/RoomCuller';
import { SinkSystem } from './world/SinkSystem';
import { ToiletFlushSystem } from './world/ToiletFlush';
import { VentGrateSystem } from './world/VentGrate';
import { EnemySystem } from './enemies/EnemySystem';
import { PistolCalib } from './debug/PistolCalib';
import { ViewmodelCalib, type ViewmodelCalibId } from './debug/ViewmodelCalib';
import { HandCalib } from './debug/HandCalib';
import { InspectProbe } from './debug/InspectProbe';
import { WEAPON_HUD_COLOR, WEAPON_ORDER, WEAPON_SLOTS, pickSlotWeapon, type WeaponId } from './weapons/WeaponDefs';
import { startVersionCheck } from './versionCheck';
import { ProjectileSystem } from './weapons/Projectiles';
import { BreakFx } from './world/BreakFx';
import { ExplosionFx } from './world/ExplosionFx';
import { ToiletSpraySystem } from './world/ToiletSpray';
import { ShotLineSystem } from './world/ShotLines';
import { AlarmSystem } from './world/AlarmSystem';
import type { Brush } from './physics/Brush';

export class GameApp {
  private readonly canvas: HTMLCanvasElement;
  private readonly overlay: HTMLElement;
  private readonly debugEl: HTMLElement;
  private readonly crosshair: HTMLElement;
  private readonly playBtn: HTMLButtonElement;
  private readonly fullscreenBtn: HTMLButtonElement;
  private readonly mirrorQualityToggle: HTMLInputElement;
  private readonly ammoEl: HTMLElement;
  private readonly ammoTextEl: HTMLElement;
  private readonly weaponHudEl: HTMLElement;
  private readonly weaponHudBoxes: HTMLElement[] = [];
  private readonly playReloadBtn: HTMLButtonElement;
  private readonly pickupToastEl: HTMLElement;
  private readonly pickupTipEl: HTMLElement;
  private readonly speechTipsEl: HTMLElement;
  private readonly speechTipPool: HTMLElement[] = [];
  private readonly dmgVignetteEl: HTMLElement;
  private readonly hpPulseEl: HTMLElement;
  private readonly hpHudEl: HTMLElement;
  private readonly hpHudTextEl: HTMLElement;
  private readonly hpHudFillEl: HTMLElement;
  private readonly dmgWedgeEl: HTMLElement;
  private readonly taglineEl: HTMLElement;
  private toastT = 0;

  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene: THREE.Scene;
  private readonly world: CapsuleWorld;
  private readonly player: PlayerController;
  private avatar: PlayerAvatar | null;
  private readonly viewmodel: Viewmodel;
  private readonly pickups: PickupSystem;
  private readonly doors: DoorSystem;
  private readonly lampFlicker: LampFlicker | null;
  private readonly roomLampDim: RoomLampDim | null;
  private readonly sinks: SinkSystem;
  private readonly toilets: ToiletFlushSystem;
  private readonly ventGrate: VentGrateSystem;
  private readonly enemies: EnemySystem;
  private readonly projectiles: ProjectileSystem;
  private readonly breakFx: BreakFx;
  private readonly explosionFx: ExplosionFx;
  private readonly toiletSpray: ToiletSpraySystem;
  private readonly shotLines: ShotLineSystem;
  private readonly alarms: AlarmSystem;
  private readonly roomCuller: RoomCuller | null;
  private readonly rooms: FacilityRoom[];
  private readonly brushes: Brush[];
  private readonly inspect: InspectProbe;
  private readonly cullDynamics: THREE.Object3D[] = [];
  private readonly input: Input;
  private readonly audio = new AudioManager();
  private readonly facilitySpawns: ReturnType<typeof buildFacilityArena>['enemySpawns'];
  private readonly facilityPickups: ReturnType<typeof buildFacilityArena>['pickups'];
  private readonly pistolCalibMode: boolean;
  private readonly viewmodelCalibWeapon: ViewmodelCalibId | null;
  private readonly handsCalibWeapon: WeaponId | null;
  private calib: PistolCalib | null = null;
  private vmCalib: ViewmodelCalib | null = null;
  private handCalib: HandCalib | null = null;

  private readonly clock = new THREE.Clock();
  private running = false;
  private assetsReady = false;
  private raf = 0;

  private frames = 0;
  private fpsTimer = 0;
  private fps = 0;
  private shadowWarm = 3;
  private readonly lookDir = new THREE.Vector3();

  private readonly onResize: () => void;
  private readonly stopVersionCheck: () => void;

  constructor() {
    this.canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    this.overlay = document.getElementById('overlay') as HTMLElement;
    this.debugEl = document.getElementById('debug') as HTMLElement;
    this.crosshair = document.getElementById('crosshair') as HTMLElement;
    this.playBtn = document.getElementById('play-btn') as HTMLButtonElement;
    this.fullscreenBtn = document.getElementById('fullscreen-btn') as HTMLButtonElement;
    this.mirrorQualityToggle = document.getElementById('mirror-quality-toggle') as HTMLInputElement;
    this.ammoEl = document.getElementById('ammo') as HTMLElement;
    this.ammoTextEl = document.getElementById('ammo-text') as HTMLElement;
    this.weaponHudEl = document.getElementById('weapon-hud') as HTMLElement;
    for (const { slot, ids } of WEAPON_SLOTS) {
      const box = document.createElement('div');
      box.className = 'weapon-slot';
      box.style.setProperty('--wep', WEAPON_HUD_COLOR[ids[0]!]);
      box.innerHTML = `<span class="weapon-slot-num">${slot}</span><span class="weapon-slot-ammo">–</span>`;
      this.weaponHudEl.appendChild(box);
      this.weaponHudBoxes.push(box);
    }
    this.playReloadBtn = document.getElementById('play-reload-btn') as HTMLButtonElement;
    this.playReloadBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.viewmodel.playReloadPreview();
    });
    this.pickupToastEl = document.getElementById('pickup-toast') as HTMLElement;
    this.pickupTipEl = document.getElementById('pickup-tip') as HTMLElement;
    this.speechTipsEl = document.getElementById('speech-tips') as HTMLElement;
    this.dmgVignetteEl = document.getElementById('dmg-vignette') as HTMLElement;
    this.hpPulseEl = document.getElementById('hp-pulse') as HTMLElement;
    this.hpHudEl = document.getElementById('hp-hud') as HTMLElement;
    this.hpHudTextEl = document.getElementById('hp-hud-text') as HTMLElement;
    this.hpHudFillEl = document.getElementById('hp-hud-fill') as HTMLElement;
    this.dmgWedgeEl = document.getElementById('dmg-wedge') as HTMLElement;
    this.taglineEl = document.querySelector('.tagline') as HTMLElement;
    this.inspect = new InspectProbe(document.getElementById('inspect-probe') as HTMLElement);

    const parsedCalib = parseCalibQuery();
    this.pistolCalibMode = parsedCalib.pistol;
    this.viewmodelCalibWeapon = parsedCalib.viewmodel;
    this.handsCalibWeapon = parsedCalib.hands;
    const studioMode = this.viewmodelCalibWeapon != null || this.handsCalibWeapon != null;

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: studioMode,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.25));
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.7;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.BasicShadowMap;
    this.renderer.shadowMap.autoUpdate = false;
    this.renderer.autoClear = false;
    this.renderer.info.autoReset = false;

    this.scene = new THREE.Scene();
    if (studioMode) {
      this.scene.background = new THREE.Color(0x12100e);
      this.scene.fog = null;
    } else {
      this.scene.background = new THREE.Color(0x1a1e24);
      this.scene.fog = new THREE.FogExp2(0x1a1e24, 0.018);
      this.setupLights();
    }

    const arena = studioMode ? buildViewmodelStudio() : buildFacilityArena();
    this.facilitySpawns = arena.enemySpawns;
    this.facilityPickups = arena.pickups;
    this.scene.add(arena.group);
    // Ceiling lights are parented under fixture meshes in the arena group

    this.doors = new DoorSystem(arena.group, arena.mats, arena.doors, arena.brushes, this.audio);
    this.sinks = new SinkSystem(this.scene, this.audio);
    this.toilets = new ToiletFlushSystem(this.scene, this.audio);
    this.breakFx = new BreakFx(this.scene);
    this.ventGrate = new VentGrateSystem(this.scene, this.audio);
    this.explosionFx = new ExplosionFx(this.scene, arena.group);
    this.toiletSpray = new ToiletSpraySystem(this.scene);
    this.shotLines = new ShotLineSystem(this.scene);
    this.alarms = new AlarmSystem(this.scene, this.audio);

    this.world = new CapsuleWorld();
    this.world.setBrushes(arena.brushes);
    if (!studioMode) {
      bakeFacilityLighting(arena.group, arena.bakeLights, arena.rooms);
      this.doors.sealAfterBake();
      this.lampFlicker = new LampFlicker(arena.group);
      this.roomLampDim = new RoomLampDim(arena.group);
    } else {
      this.lampFlicker = null;
      this.roomLampDim = null;
    }
    this.rooms = arena.rooms;
    this.brushes = arena.brushes;
    this.roomCuller = studioMode ? null : new RoomCuller(arena.group, arena.rooms);
    if (this.roomCuller) {
      const culler = this.roomCuller;
      setMirrorScenePrep((hide) => {
        if (hide) culler.hideExclusiveRooms(['vent']);
        else culler.restoreExclusiveRooms();
      });
    } else {
      setMirrorScenePrep(null);
    }

    this.player = new PlayerController(this.world, arena.spawn);
    this.player.setSpawn(arena.spawn, arena.spawnYaw, { crouch: arena.spawnCrouch });
    if (!studioMode) {
      this.player.camera.far = 110;
      this.player.camera.updateProjectionMatrix();
    }
    this.avatar = null;

    this.viewmodel = new Viewmodel(this.audio);
    this.viewmodel.attachWorldFx(this.scene);
    this.enemies = new EnemySystem(this.scene, this.world, this.audio);
    this.enemies.bindDoors(this.doors);
    this.enemies.bindAlarms(this.alarms);
    this.alarms.onSounded = () => {
      this.enemies.alertFacility();
      this.pickupToastEl.textContent = 'ALARM';
      this.pickupToastEl.classList.add('visible');
      this.toastT = 2.6;
    };
    if (!studioMode) this.enemies.bindRooms(arena.rooms);
    this.enemies.onHostileKilled = (pos) => this.maybeDropEmergencyMedkit(pos);
    this.viewmodel.setEnemyHitHandler((obj, damage, point, dir, weaponId, meleeStun) => {
      this.enemies.notePlayer(this.player);
      return this.enemies.applyHit(obj, damage, point, dir, weaponId, meleeStun);
    });
    this.viewmodel.setShotHandler((shot) => {
      if (shot.weapon.id === 'punch' || shot.pelletIndex > 0) return;
      this.enemies.hearShot(shot.origin, this.player, shot.silenced === true);
    });
    this.viewmodel.setSmashHandler((origin) => this.enemies.hearSmash(origin));
    this.viewmodel.setThudHandler((origin) => this.enemies.hearThud(origin));
    this.projectiles = new ProjectileSystem(this.scene);
    this.viewmodel.setProjectileSystem(this.projectiles);
    this.viewmodel.setToiletSpray(this.toiletSpray);
    this.viewmodel.setShotLines(this.shotLines);
    this.enemies.setShotLines(this.shotLines);
    this.projectiles.setBounceHandler((at, bounce) => {
      this.audio.playAt('grenadeBounce', at, this.player.camera.position, {
        volume: 0.5,
        rate: 0.92 + (bounce - 1) * 0.055 + Math.random() * 0.03,
        range: 30,
      });
    });
    this.projectiles.setSplashHandler((e) => {
      this.enemies.applySplash(e.origin, e.radius, e.damage, e.weaponId);
      this.explosionFx.burst(e.origin, e.radius, this.player.camera.position, e.weaponId);
      this.player.addBlastShake(e.origin, e.radius);
      const dx = this.player.camera.position.x - e.origin.x;
      const dy = this.player.camera.position.y - e.origin.y;
      const dz = this.player.camera.position.z - e.origin.z;
      const dist = Math.hypot(dx, dy, dz);
      if (dist < e.radius) {
        const falloff = 1 - dist / e.radius;
        this.player.takeDamage(Math.max(1, Math.round(e.damage * falloff * 0.55)), e.origin);
        this.audio.play('hitBody', { volume: 0.55, rate: 0.92 + Math.random() * 0.14 });
      }
      if (e.weaponId === 'gl') {
        this.audio.playGrenadeExplode(e.origin, this.player.camera.position);
      } else {
        this.audio.playAt('rocketExplode', e.origin, this.player.camera.position, {
          volume: 0.9,
          range: 52,
        });
        this.audio.playAt('explosionAmbient', e.origin, this.player.camera.position, {
          volume: 0.32,
          range: 64,
        });
      }
      this.viewmodel.shatterInRadius(e.origin, e.radius, this.scene);
      this.doors.blastAt(e.origin, e.radius, this.breakFx);
      this.breakExplodables(e.origin, e.radius);
    });
    this.pickups = new PickupSystem(this.scene, this.audio);
    this.viewmodel.setCrateBreakHandler((crate) => this.breakCrate(crate));
    this.viewmodel.setLampBreakHandler((lamp) => this.breakLamp(lamp));
    this.viewmodel.setGrateBreakHandler((obj) => this.ventGrate.breakFromShot(obj));
    this.viewmodel.setAlarmBreakHandler((obj) => this.breakAlarm(obj));
    this.input = new Input(this.canvas);

    this.playBtn.disabled = true;
    this.playBtn.textContent = 'Loading weapons…';
    this.taglineEl.textContent = this.pistolCalibMode
      ? 'Pistol calib scene — loading…'
      : this.viewmodelCalibWeapon
        ? `${this.viewmodelCalibWeapon.toUpperCase()} viewmodel studio — loading…`
        : this.handsCalibWeapon
          ? `Hand attach studio (${this.handsCalibWeapon}) — loading…`
          : 'Loading…';

    // Playwright / calib harness
    (window as unknown as { __GAME__: unknown }).__GAME__ = {
      enemies: this.enemies,
      player: this.player,
      viewmodel: this.viewmodel,
      scene: this.scene,
      isReady: () => this.assetsReady,
      isRunning: () => this.running,
      startPlay: () => void this.startPlay(),
    };

    void this.bootAssets();

    this.playBtn.addEventListener('click', () => void this.startPlay());
    this.fullscreenBtn.addEventListener('click', () => void this.toggleFullscreen());
    this.mirrorQualityToggle.addEventListener('change', () => {
      setMirrorQuality(this.mirrorQualityToggle.checked ? 'high' : 'crap');
    });
    document.addEventListener('fullscreenchange', () => {
      this.syncFullscreenBtn();
      if (this.running && this.input.isLocked) void this.input.relockKeyboard();
    });
    this.syncFullscreenBtn();
    this.mirrorQualityToggle.checked = getMirrorQuality() === 'high';

    this.canvas.addEventListener('click', () => {
      if (this.pistolCalibMode || this.viewmodelCalibWeapon || this.handsCalibWeapon) return;
      if (this.running && !this.input.isLocked) void this.input.requestLock();
    });

    // Pause menu only when play capture ends (Esc). Do NOT key off raw
    // pointerlockchange — Cursor's browser often denies/revokes lock instantly.
    window.addEventListener('keydown', (e) => {
      if (this.pistolCalibMode || this.viewmodelCalibWeapon || this.handsCalibWeapon) return;
      if (e.code !== 'Escape') return;
      queueMicrotask(() => {
        if (this.running && !this.input.isLocked) this.showOverlay(true);
      });
    });

    this.onResize = () => this.handleResize();
    window.addEventListener('resize', this.onResize);
    this.handleResize();
    this.stopVersionCheck = startVersionCheck();

    this.debugEl.classList.remove('hidden');
    this.clock.start();
    this.loop();
  }

  private async bootAssets(): Promise<void> {
    try {
      if (this.viewmodelCalibWeapon) {
        await this.viewmodel.load();
        await this.viewmodel.warmGpu(this.renderer, this.scene, this.player.camera);
        this.assetsReady = true;
        this.enterViewmodelCalib();
        return;
      }
      if (this.handsCalibWeapon) {
        await this.viewmodel.load();
        await this.viewmodel.warmGpu(this.renderer, this.scene, this.player.camera);
        this.assetsReady = true;
        this.enterHandCalib();
        return;
      }
      await Promise.all([
        this.viewmodel.load(),
        this.pickups.load(this.facilityPickups),
        this.enemies.load(),
      ]);
      this.enemies.spawnAll(this.facilitySpawns);
      await this.viewmodel.warmGpu(this.renderer, this.scene, this.player.camera);
      this.avatar = new PlayerAvatar(this.scene, { previewInFront: false });
      warmMirrors(this.renderer, this.scene, this.player.camera);
      setMirrorsLive(true);
      this.assetsReady = true;
      if (this.pistolCalibMode) {
        this.enterPistolCalib();
      } else {
        this.playBtn.disabled = false;
        this.playBtn.textContent = 'Click to Play';
        this.taglineEl.textContent = 'Facility — clear the corridors';
      }
    } catch (err) {
      console.error(err);
      this.playBtn.textContent = 'Load failed — see console';
      this.taglineEl.textContent = 'Asset load error';
    }
  }

  private enterPistolCalib(): void {
    this.calib = new PistolCalib(
      this.scene,
      this.player.camera,
      this.canvas,
      this.enemies,
    );
    const ok = this.calib.start();
    if (!ok) {
      this.taglineEl.textContent = 'Calib failed — see console';
      this.playBtn.disabled = false;
      this.playBtn.textContent = 'Click to Play';
      return;
    }
    this.running = true;
    this.showOverlay(false);
    this.crosshair.classList.add('hidden');
    this.ammoEl.classList.add('hidden');
    this.weaponHudEl.classList.add('hidden');
    this.pickupToastEl.classList.add('hidden');
    this.clock.start();
    this.taglineEl.textContent = 'Pistol calib active';
  }

  private enterViewmodelCalib(): void {
    if (!this.viewmodelCalibWeapon) return;
    this.vmCalib = new ViewmodelCalib(this.viewmodel, this.viewmodelCalibWeapon);
    this.vmCalib.start();
    this.player.placeForCalib(
      this.player.body.position.x,
      this.player.body.position.y,
      this.player.body.position.z,
      0,
      0,
    );
    this.running = true;
    this.showOverlay(false);
    this.crosshair.classList.remove('hidden');
    this.ammoEl.classList.add('hidden');
    this.weaponHudEl.classList.add('hidden');
    this.pickupToastEl.classList.add('hidden');
    this.clock.start();
    this.taglineEl.textContent = `${this.viewmodelCalibWeapon.toUpperCase()} viewmodel studio`;
  }

  private ensureAvatar(): PlayerAvatar {
    if (!this.avatar) {
      this.avatar = new PlayerAvatar(this.scene, { previewInFront: false });
    }
    return this.avatar;
  }

  private enterHandCalib(): void {
    if (!this.handsCalibWeapon) return;
    this.handCalib = new HandCalib(
      this.viewmodel,
      this.handsCalibWeapon,
      this.canvas,
      () => this.ensureAvatar(),
      this.player,
    );
    this.handCalib.start();
    this.player.placeForCalib(
      this.player.body.position.x,
      this.player.body.position.y,
      this.player.body.position.z,
      0,
      0,
    );
    this.running = true;
    this.showOverlay(false);
    this.crosshair.classList.remove('hidden');
    this.ammoEl.classList.add('hidden');
    this.weaponHudEl.classList.add('hidden');
    this.pickupToastEl.classList.add('hidden');
    this.clock.start();
    this.taglineEl.textContent = `Hand attach studio — ${this.handsCalibWeapon}`;
  }

  private setupLights(): void {
    const hemi = new THREE.HemisphereLight(0xd8e0ea, 0x4a5048, 1.05);
    this.scene.add(hemi);

    const key = new THREE.DirectionalLight(0xf6f7fa, 1.32);
    key.position.set(0, 42, 28);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.camera.near = 2;
    key.shadow.camera.far = 140;
    key.shadow.camera.left = -40;
    key.shadow.camera.right = 40;
    key.shadow.camera.top = 40;
    key.shadow.camera.bottom = -40;
    key.shadow.bias = -0.0004;
    this.scene.add(key);

    const fill = new THREE.DirectionalLight(0xb8c6d8, 0.66);
    fill.position.set(16, 10, -14);
    this.scene.add(fill);
  }

  private async startPlay(): Promise<void> {
    if (!this.assetsReady) return;
    this.running = true;
    this.showOverlay(false);
    this.crosshair.classList.remove('hidden');
    this.ammoEl.classList.remove('hidden');
    this.weaponHudEl.classList.remove('hidden');
    this.pickupToastEl.classList.remove('hidden');
    document.body.classList.add('playing');
    this.clock.start();
    await this.audio.unlock();
    this.audio.playMusic();
    await this.input.requestLock();
    if (this.input.usingFallbackLook) {
      this.taglineEl.textContent =
        'Pointer lock unavailable here — move mouse to look, Esc to pause';
    }
  }

  private breakExplodables(origin: THREE.Vector3, radius: number): void {
    const hit: THREE.Object3D[] = [];
    this.scene.traverse((o) => {
      const kind = o.userData?.destructible;
      if (
        (kind !== 'wall' &&
          kind !== 'crate' &&
          kind !== 'lamp' &&
          kind !== 'grate' &&
          kind !== 'alarm') ||
        o.userData.destroyed
      ) {
        return;
      }
      const c = new THREE.Vector3();
      o.getWorldPosition(c);
      if (c.distanceTo(origin) <= radius + 0.45) hit.push(o);
    });
    for (const o of hit) {
      if (o.userData.destructible === 'crate') {
        this.breakCrate(o);
        continue;
      }
      if (o.userData.destructible === 'lamp') {
        this.breakLamp(o);
        continue;
      }
      if (o.userData.destructible === 'grate') {
        this.ventGrate.breakFromShot(o);
        continue;
      }
      if (o.userData.destructible === 'alarm') {
        this.breakAlarm(o);
        continue;
      }
      o.userData.destroyed = true;
      o.userData.ignoreHit = true;
      o.visible = false;
      const brush = o.userData.brush as Brush | undefined;
      if (brush) brush.noCollision = true;
      const box = new THREE.Box3().setFromObject(o);
      const center = box.getCenter(new THREE.Vector3());
      this.breakFx.spawnChunks(center, box.getSize(new THREE.Vector3()), 0x6a6458, 18, 'rubble', this.chunkFloor(center));
    }
  }

  private chunkFloor(at: THREE.Vector3): number {
    return floorBelow(this.rooms, at) + 0.04;
  }

  private dropCratePapers(box: THREE.Box3): void {
    const floorY = this.chunkFloor(box.getCenter(new THREE.Vector3()));
    const pad = 0.4;
    const papers: THREE.Object3D[] = [];
    this.scene.traverse((o) => {
      if (o.userData?.looseProp !== 'paper' || o.userData.dropped) return;
      const p = new THREE.Vector3();
      o.getWorldPosition(p);
      if (p.x < box.min.x - pad || p.x > box.max.x + pad) return;
      if (p.z < box.min.z - pad || p.z > box.max.z + pad) return;
      if (p.y < box.min.y - 0.15 || p.y > box.max.y + 0.4) return;
      papers.push(o);
    });
    for (const p of papers) this.breakFx.dropProp(p, floorY);
  }

  private breakCrate(o: THREE.Object3D): void {
    if (o.userData.destroyed || o.userData.destructible !== 'crate') return;
    o.userData.destroyed = true;
    o.userData.ignoreHit = true;
    o.visible = false;
    const brush = o.userData.brush as Brush | undefined;
    if (brush) brush.noCollision = true;
    const box = new THREE.Box3().setFromObject(o);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const wood = o.userData.crateStyle === 'wood';
    const floorY = this.chunkFloor(center);
    this.dropCratePapers(box);
    this.breakFx.spawnChunks(center, size, wood ? 0x8a6a40 : 0x4a5340, 16, 'plank', floorY);
    this.audio.playHit(wood ? 'wood' : 'metal', { volume: 0.5 });
    this.enemies.hearSmash(center);
    const loot = o.userData.crateLoot as PickupKind | null | undefined;
    if (loot) this.pickups.spawnAt(loot, center.x, center.y + 0.18, center.z);
    if (!brush) return;
    this.scene.traverse((s) => {
      if (s === o || s.userData?.destructible !== 'crate' || s.userData.destroyed) return;
      if (s.userData.brush) return;
      const c = new THREE.Vector3();
      s.getWorldPosition(c);
      if (c.distanceTo(center) < 1.25) this.breakCrate(s);
    });
  }

  private breakAlarm(obj: THREE.Object3D): void {
    const panel = this.alarms.destroy(obj);
    if (!panel) return;
    const box = new THREE.Box3().setFromObject(panel);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    panel.visible = false;
    const floorY = this.chunkFloor(center);
    this.breakFx.spawnChunks(center, size, 0x6a6860, 8, 'plank', floorY);
    this.breakFx.spawnChunks(center, size.clone().multiplyScalar(0.55), 0xa01810, 6, 'rubble', floorY);
    this.audio.playHit('metal', { volume: 0.55 });
    this.audio.playAt('switchClick', center, this.player.camera.position, {
      volume: 0.45,
      rate: 1.35 + Math.random() * 0.2,
      range: 18,
    });
    this.enemies.hearSmash(center);
  }

  private breakLamp(lamp: THREE.Object3D): void {
    if (lamp.userData.destroyed || lamp.userData.destructible !== 'lamp') return;
    lamp.userData.destroyed = true;
    lamp.userData.ignoreHit = true;
    const box = new THREE.Box3().setFromObject(lamp);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const parked = lamp.userData.lampLight as THREE.PointLight | undefined;
    if (parked) parked.intensity = 0;
    const beacon = lamp.userData.alarmBeacon === true;
    if (beacon) {
      lamp.visible = false;
    } else {
      const hide = (o: THREE.Object3D | undefined) => {
        if (o) o.visible = false;
      };
      hide(lamp.userData.lampTube as THREE.Object3D | undefined);
      hide(lamp.userData.lampGlow as THREE.Object3D | undefined);
      lamp.traverse((c) => {
        if (c.name === 'lamp_lip' || c.name === 'lamp_tube' || c.name === 'lamp_glow') c.visible = false;
      });
      this.roomLampDim?.noteBreak(lamp);
    }
    const floorY = this.chunkFloor(center);
    const burst = center.clone();
    burst.y -= 0.14;
    this.breakFx.spawnChunks(burst, size, beacon ? 0x4a3030 : 0x6a7080, 10, 'plank', floorY);
    this.breakFx.spawnChunks(
      burst,
      size.clone().multiplyScalar(0.7),
      beacon ? 0xff2a10 : 0xdde6f0,
      8,
      'rubble',
      floorY,
    );
    this.audio.play('lampBreak', { volume: 0.5, rate: 1.1 + Math.random() * 0.2 });
    this.audio.play('glassBreak', { volume: 0.28, rate: 1.35 + Math.random() * 0.2 });
    this.enemies.hearSmash(center);
  }

  private async toggleFullscreen(): Promise<void> {
    await this.input.toggleFullscreen();
    this.syncFullscreenBtn();
  }

  private syncFullscreenBtn(): void {
    this.fullscreenBtn.textContent = this.input.isFullscreen ? 'Exit Fullscreen' : 'Fullscreen';
  }

  private showOverlay(show: boolean): void {
    if (show) {
      this.overlay.classList.remove('hidden');
      this.crosshair.classList.add('hidden');
      this.ammoEl.classList.add('hidden');
      this.weaponHudEl.classList.add('hidden');
      this.pickupToastEl.classList.add('hidden');
      this.pickupToastEl.classList.remove('visible');
      this.pickupTipEl.classList.remove('visible');
      document.body.classList.remove('playing');
      this.playBtn.textContent = 'Click to Play';
    } else {
      this.overlay.classList.add('hidden');
    }
  }

  private syncRoomFog(): void {
    const fog = this.scene.fog;
    if (!(fog instanceof THREE.FogExp2)) return;
    const fy = roomAt(this.rooms, this.player.camera.position)?.floorY ?? 0;
    fog.density = fy < -1 ? 0.024 : 0.018;
  }

  private handleResize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h, false);
    const aspect = w / Math.max(h, 1);
    this.player.camera.aspect = aspect;
    this.player.camera.updateProjectionMatrix();
    this.viewmodel.resize(aspect);
  }

  private loop = (): void => {
    this.raf = requestAnimationFrame(this.loop);
    const dt = Math.min(this.clock.getDelta(), 0.05);

    if (this.running) {
      if (this.vmCalib?.isActive || this.handCalib?.isActive) {
        if (this.handCalib?.drawViewmodel !== false) this.player.syncCameraOnly();
        this.viewmodel.update(this.player, this.input, dt, this.scene);
        this.projectiles.update(dt, this.scene, this.player);
        this.breakFx.update(dt);
        this.toiletSpray.update(dt);
        this.shotLines.update(dt);
        this.vmCalib?.update();
        this.handCalib?.update();
        this.syncPickupTip(false);
        this.syncSpeechTips(false);
      } else if (this.calib?.isActive) {
        this.enemies.update(this.player, dt);
        this.shotLines.update(dt);
        this.calib.update();
        this.syncPickupTip(false);
        this.syncSpeechTips(false);
      } else if (this.input.isLocked) {
        this.player.update(this.input, dt);
        if (this.player.consumeStep()) {
          const pos = this.player.body.position;
          const spd = Math.hypot(this.player.body.velocity.x, this.player.body.velocity.z);
          const surface = isInVent(pos) ? 'vent' : isInBathroom(pos) ? 'tile' : 'default';
          this.audio.playFootstep(surface, spd);
        }
        const usedGrate = this.ventGrate.update(this.player, this.input, dt);
        const usedSink = this.sinks.update(this.player, this.input, dt, !usedGrate);
        const usedToilet = this.toilets.update(this.player, this.input, dt, !usedGrate && !usedSink);
        const usedAlarm = this.alarms.update(
          this.player,
          this.input,
          !usedGrate && !usedSink && !usedToilet,
          dt,
        );
        const usedTalk =
          !usedSink &&
          !usedToilet &&
          !usedGrate &&
          !usedAlarm &&
          this.input.interactPressed &&
          this.enemies.tryTalk(this.player);
        this.doors.update(
          this.player,
          this.input,
          dt,
          !usedSink && !usedToilet && !usedGrate && !usedTalk && !usedAlarm,
        );
        this.player.unstick();
        this.viewmodel.update(this.player, this.input, dt, this.scene);
        this.pickups.update(this.player, this.viewmodel, dt);
        this.enemies.update(this.player, dt, this.viewmodel.fistsUp);
        this.projectiles.update(dt, this.scene, this.player);
        this.breakFx.update(dt);
        this.toiletSpray.update(dt);
        this.shotLines.update(dt);
        const toast = this.pickups.consumeToast() ?? this.viewmodel.consumeToast();
        if (toast) {
          this.pickupToastEl.textContent = toast;
          this.pickupToastEl.classList.add('visible');
          this.toastT = 2.1;
        }
        this.syncPickupTip(true);
        this.syncSpeechTips(true);
        if (this.input.musicTogglePressed) {
          const on = this.audio.toggleMusic();
          this.pickupToastEl.textContent = on ? 'Music on' : 'Music off — press M to enable';
          this.pickupToastEl.classList.add('visible');
          this.toastT = 2.4;
        }
      } else {
        // Keep camera synced when frozen for screenshots
        this.player.syncCameraOnly();
        this.enemies.update(this.player, dt, this.viewmodel.fistsUp);
        this.syncPickupTip(false);
        this.syncSpeechTips(false);
      }
    } else {
      this.syncPickupTip(false);
      this.syncSpeechTips(false);
    }

    this.avatar?.update(this.player, dt, this.viewmodel);

    if (this.input.inspectPressed) {
      this.inspect.toggle({
        eye: this.player.camera.position,
        feet: this.player.body.position,
        yaw: this.player.yaw,
        pitch: this.player.pitch,
        camera: this.player.camera,
        scene: this.scene,
        brushes: this.brushes,
        rooms: this.rooms,
      });
    }
    if (this.input.copyDebugPressed && this.inspect.open) {
      void this.inspect.copyToClipboard(this.debugEl.textContent ?? '').then((ok) => {
        if (!ok) return;
        this.pickupToastEl.textContent = 'Copied debug information';
        this.pickupToastEl.classList.add('visible');
        this.toastT = 2.2;
      });
    }
    this.inspect.update(dt);

    if (this.player.updateShake(dt)) this.player.syncCameraOnly();
    this.explosionFx.update(dt);

    this.audio.setListener(this.player.camera.position, this.player.yaw, this.player.pitch);

    if (this.toastT > 0) {
      this.toastT -= dt;
      if (this.toastT <= 0) this.pickupToastEl.classList.remove('visible');
    }

    if (this.roomCuller) {
      this.cullDynamics.length = 0;
      this.enemies.collectCullRoots(this.cullDynamics);
      this.pickups.collectCullRoots(this.cullDynamics);
      this.player.camera.getWorldDirection(this.lookDir);
      const seeVentFromBath =
        this.lookDir.y > 0.28 ||
        this.ventGrate.anyOpen() ||
        isInVent(this.player.body.position) ||
        this.player.camera.position.y > 2.85;
      this.roomCuller.update(this.player.camera, this.player.camera.position, this.cullDynamics, {
        seeVentFromBath,
      });
      this.enemies.setVisibleRooms(this.roomCuller.visibleRoomIds());
    }
    this.lampFlicker?.update(dt);
    this.syncRoomFog();

    if (
      this.shadowWarm > 0 ||
      this.doors.anyAnimating() ||
      this.ventGrate.anyMotion() ||
      this.enemies.anyMoving()
    ) {
      this.renderer.shadowMap.needsUpdate = true;
      if (this.shadowWarm > 0) this.shadowWarm -= 1;
    }

    if (this.assetsReady) {
      this.renderer.info.reset();
      this.renderer.clear();
      this.renderer.render(this.scene, this.player.camera);
      if (!this.calib?.isActive && (this.handCalib?.drawViewmodel ?? true)) {
        this.renderer.clearDepth();
        this.renderer.render(this.viewmodel.scene, this.viewmodel.camera);
      }
    }

    this.input.endFrame();
    this.updateHud(dt);
  };

  private updateHud(dt: number): void {
    this.syncDamageFx(dt);
    this.frames += 1;
    this.fpsTimer += dt;
    if (this.fpsTimer >= 0.5) {
      this.fps = Math.round(this.frames / this.fpsTimer);
      this.frames = 0;
      this.fpsTimer = 0;
    }

    const w = this.viewmodel.currentWeapon;
    if (this.vmCalib?.isActive || this.handCalib?.isActive) {
      this.debugEl.textContent = [
        this.handCalib?.isActive ? `Hand attach — ${w.name}` : `Viewmodel studio — ${w.name}`,
        `FPS: ${this.fps}`,
        `ADS: ${this.viewmodel.adsBlend >= 0.5 ? 'ON' : 'off'}`,
        `Copy the snippet on the right, then paste it in chat.`,
      ].join('\n');
      const scale = 1 - this.viewmodel.adsBlend * 0.45;
      this.crosshair.style.transform = `translate(-50%, -50%) scale(${scale})`;
      this.crosshair.style.opacity = this.handCalib?.drawViewmodel === false ? '0' : String(0.7 + this.viewmodel.adsBlend * 0.25);
      return;
    }

    const p = this.player.body.position;
    const v = this.player.body.velocity;
    const gpu = this.renderer.info.render;
    const mirrorAge = mirrorLastAgeMs();
    this.debugEl.textContent = [
      `GOLDEN AI 007 — M2`,
      `FPS: ${this.fps}`,
      `calls: ${gpu.calls}  tris: ${gpu.triangles}`,
      `rooms: ${this.roomCuller ? [...this.roomCuller.visibleRoomIds()].join(',') || '—' : '—'}`,
      `mirror: ${mirrorAge < 0 ? '—' : `${Math.round(mirrorAge)}ms`}`,
      `HP: ${this.player.health}  ARM: ${this.player.armor}`,
      `pos: ${p.x.toFixed(2)} ${p.y.toFixed(2)} ${p.z.toFixed(2)}`,
      `vel: ${v.x.toFixed(2)} ${v.y.toFixed(2)} ${v.z.toFixed(2)}`,
      `grounded: ${this.player.body.grounded}`,
      `weapon: ${w.name}  ADS: ${this.viewmodel.adsBlend.toFixed(2)}`,
      `slide: ${this.player.isSliding ? 'YES' : 'no'} (${this.player.slideBlend.toFixed(2)})`,
      `vm: ${this.viewmodel.isReady ? this.viewmodel.viewmodelKind : 'loading'}`,
      `locked: ${this.input.isLocked}${this.input.usingFallbackLook ? ' (fallback look)' : ''}`,
    ].join('\n');

    const hp = Math.max(0, Math.round(this.player.health));
    const hpRatio = hp / this.player.maxHealth;
    this.hpHudTextEl.textContent = `HP ${hp}`;
    this.hpHudFillEl.style.transform = `scaleX(${Math.max(0, Math.min(1, hpRatio))})`;
    this.hpHudEl.classList.toggle('low', hpRatio > 0.3 && hpRatio <= 0.55);
    this.hpHudEl.classList.toggle('critical', hpRatio > 0 && hpRatio <= 0.3);
    this.ammoTextEl.textContent = `${this.viewmodel.hudWeaponName.toUpperCase()}\n${this.viewmodel.ammoString()}\nARM ${this.player.armor}`;
    this.syncWeaponHud();
    this.playReloadBtn.classList.toggle(
      'hidden',
      w.id !== 'sawedoff' && w.id !== 'shotgun' && w.id !== 'pistol' && w.id !== 'mp5' && w.id !== 'ak47',
    );

    const scale = 1 - this.viewmodel.adsBlend * 0.45;
    this.crosshair.style.transform = `translate(-50%, -50%) scale(${scale})`;
    this.crosshair.style.opacity = String(0.55 + this.viewmodel.adsBlend * 0.35);
  }

  private syncPickupTip(playing: boolean): void {
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    const tip =
      playing && this.assetsReady
        ? (this.pickups.lookTip(this.player.camera, this.scene, w, h) ??
          this.ventGrate.lookTip(this.player, this.player.camera, w, h) ??
          this.sinks.lookTip(this.player, this.player.camera, w, h) ??
          this.toilets.lookTip(this.player, this.player.camera, w, h) ??
          this.alarms.lookTip(this.player, this.player.camera, w, h) ??
          this.enemies.lookTalkTip(this.player, this.player.camera, w, h) ??
          this.doors.lookTip(this.player, this.player.camera, w, h))
        : null;
    if (!tip) {
      this.pickupTipEl.classList.remove('visible');
      return;
    }
    if (this.pickupTipEl.textContent !== tip.label) this.pickupTipEl.textContent = tip.label;
    this.pickupTipEl.style.transform = `translate(${tip.x}px, ${tip.y}px) translate(-50%, calc(-100% - 10px))`;
    this.pickupTipEl.classList.add('visible');
  }

  private syncSpeechTips(playing: boolean): void {
    const tips =
      playing && this.assetsReady
        ? this.enemies.speechTips(
            this.player.camera,
            this.canvas.clientWidth || window.innerWidth,
            this.canvas.clientHeight || window.innerHeight,
          )
        : [];
    while (this.speechTipPool.length < tips.length) {
      const el = document.createElement('div');
      el.className = 'speech-tip';
      this.speechTipsEl.appendChild(el);
      this.speechTipPool.push(el);
    }
    for (let i = 0; i < this.speechTipPool.length; i++) {
      const el = this.speechTipPool[i]!;
      const tip = tips[i];
      if (!tip) {
        el.classList.remove('visible');
        continue;
      }
      if (el.textContent !== tip.label) el.textContent = tip.label;
      el.style.transform = `translate(${tip.x}px, ${tip.y}px) translate(-50%, calc(-100% - 12px))`;
      el.classList.add('visible');
    }
  }

  private syncWeaponHud(): void {
    const equipped = this.viewmodel.currentWeapon.id;
    for (let i = 0; i < WEAPON_SLOTS.length; i++) {
      const ids = WEAPON_SLOTS[i]!.ids;
      const box = this.weaponHudBoxes[i]!;
      const shown = ids.includes(equipped) ? equipped : pickSlotWeapon(ids, (w) => this.viewmodel.ownsWeapon(w));
      const owned = shown !== null;
      box.classList.toggle('owned', owned);
      box.classList.toggle('equipped', owned && ids.includes(equipped));
      box.style.setProperty('--wep', WEAPON_HUD_COLOR[shown ?? ids[0]!]);
      const ammoEl = box.querySelector('.weapon-slot-ammo');
      if (ammoEl) ammoEl.textContent = shown ? this.viewmodel.hudAmmo(shown) : '–';
    }
  }

  private maybeDropEmergencyMedkit(pos: THREE.Vector3): void {
    const ratio = this.player.health / this.player.maxHealth;
    if (ratio > 0.3 || this.player.health <= 0) return;
    if (this.pickups.emergencyCount() >= 2) return;
    if (Math.random() > 0.4) return;
    this.pickups.spawnAt('medi_small', pos.x, pos.y + 0.14, pos.z, true);
  }

  private syncDamageFx(dt: number): void {
    this.player.damageFlashT = Math.max(0, this.player.damageFlashT - dt);
    const hpRatio = this.player.health / this.player.maxHealth;
    const dying = this.running && hpRatio > 0 && hpRatio <= 0.3;
    this.hpPulseEl.classList.toggle('active', dying);
    this.hpPulseEl.classList.toggle('critical', dying && hpRatio <= 0.15);
    const t = this.player.damageFlashT;
    if (t <= 0) {
      this.dmgVignetteEl.style.opacity = '0';
      this.dmgWedgeEl.style.opacity = '0';
      return;
    }
    const fade = Math.min(1, t / 0.22);
    this.dmgVignetteEl.style.opacity = String(0.78 * fade);
    if (!this.player.hasLastHit) {
      this.dmgWedgeEl.style.opacity = '0';
      return;
    }
    const ax = this.player.lastHitFrom.x - this.player.camera.position.x;
    const az = this.player.lastHitFrom.z - this.player.camera.position.z;
    const yaw = this.player.yaw;
    const fwdX = -Math.sin(yaw);
    const fwdZ = -Math.cos(yaw);
    const rightX = Math.cos(yaw);
    const rightZ = -Math.sin(yaw);
    const localX = ax * rightX + az * rightZ;
    const localZ = ax * fwdX + az * fwdZ;
    const deg = (Math.atan2(localX, localZ) * 180) / Math.PI;
    this.dmgWedgeEl.style.opacity = String(0.95 * fade);
    this.dmgWedgeEl.style.transform = `translate(-50%, -50%) rotate(${deg}deg)`;
  }

  dispose(): void {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this.onResize);
    this.calib?.dispose();
    this.vmCalib?.dispose();
    this.handCalib?.dispose();
    this.stopVersionCheck();
    this.alarms.dispose();
    this.input.dispose();
    this.renderer.dispose();
  }
}

function parseCalibQuery(): {
  pistol: boolean;
  viewmodel: ViewmodelCalibId | null;
  hands: WeaponId | null;
} {
  const params = new URLSearchParams(window.location.search);
  const calib = (params.get('calib') ?? '').toLowerCase();
  const weapon = (params.get('weapon') ?? '').toLowerCase();
  if (calib === 'pistol') return { pistol: true, viewmodel: null, hands: null };
  if (calib === 'hands' || calib === 'hand' || calib === 'arms') {
    const id = WEAPON_ORDER.includes(weapon as WeaponId) ? (weapon as WeaponId) : 'pistol';
    return { pistol: false, viewmodel: null, hands: id };
  }
  if (calib === 'mp5' || calib === 'ak47') return { pistol: false, viewmodel: calib, hands: null };
  if (calib === 'viewmodel' || calib === 'gun' || calib === 'vm') {
    return { pistol: false, viewmodel: weapon === 'ak47' ? 'ak47' : 'mp5', hands: null };
  }
  return { pistol: false, viewmodel: null, hands: null };
}
